import {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {GridColDef, GridFilterModel, GridSortModel} from '@mui/x-data-grid';
import './JobResultDataGrid.scss';
import {tableProps} from '../../job-list-layout/utils';
import ArrDataGridPro from '../../../../common/arr-data-grid-pro/ArrDataGridPro';
import {getViewByDocsColumns} from './viewByDocsColumn';
import {useGetAIReviewJobResultDocsList} from '../../../../../hooks/ai/review/useReview';
import {ToastType} from '../../../../../shared/constants';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import {IPayload, IPreviewDoc, PAYLOAD_TYPE} from '../JobResults';
import {
  mapGridFiltersToColumnFilters,
  mapGridFiltersToNavigateColumnFilters,
  VIEW_BY_DOCS_COLUMN_MAP,
  VIEW_BY_DOCS_NAVIGATE_COLUMN_MAP,
} from '../../utils';
import {DataGridRowSelectionModel} from '../../../../../shared/interfaces';
import {IGetMasterDocIdForRelevancyPayloadBody} from '../../../../../api/types/aiReview.type';
import {ViewInDocListAction} from '../job-result-tabs/JobResultTabs';
import IssueCodingDocumentDetails from '../issue-coding-document-details/IssueCodingDocumentDetails';
import {ReactComponent as NoDataFoundIcon} from '../../../../../assets/images/no-data-found.svg';

interface ViewByDocsDataGridProps {
  jobId: string | number;
  handleSetStates: (
    payload: IPayload,
    type: PAYLOAD_TYPE,
    docsToView?: number,
    destination?: 'DOCLIST' | 'DOCVIEW'
  ) => void;
  onViewInDocListActionChange?: (action: ViewInDocListAction) => void;
  setSelectedDocForPreview: React.Dispatch<
    React.SetStateAction<IPreviewDoc | null>
  >;
}

type ToastState = {
  open: boolean;
  message: string;
  status: ToastType;
};

const defaultToastState: ToastState = {
  open: false,
  message: '',
  status: ToastType.Success,
};

const ViewByDocsDataGrid = ({
  jobId,
  handleSetStates,
  onViewInDocListActionChange,
  setSelectedDocForPreview,
}: ViewByDocsDataGridProps) => {
  const [pageSize, setPageSize] = useState(tableProps.DEFAULT_PAGE_SIZE);
  const [pageNumber, setPageNumber] = useState(1);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {field: 'docId', sort: 'asc'},
  ]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({items: []});
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [unselectedIds, setUnselectedIds] = useState<string[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [toast, setToast] = useState<ToastState>(defaultToastState);
  const [selectedIssueDoc, setSelectedIssueDoc] = useState<{
    reviewJobSubmittedId: number;
    docId: number;
  } | null>(null);

  const docsPayload = useMemo(
    () => ({
      sortingColumn:
        VIEW_BY_DOCS_COLUMN_MAP[sortModel[0]?.field] ??
        sortModel[0]?.field ??
        'DocId',
      direction: sortModel[0]?.sort?.toUpperCase() ?? 'ASC',
      displayLength: pageSize,
      pageNumber,
      columnFilter: mapGridFiltersToColumnFilters(
        filterModel.items,
        VIEW_BY_DOCS_COLUMN_MAP
      ),
    }),
    [sortModel, filterModel, pageSize, pageNumber]
  );

  const {
    data: docsResponse,
    isLoading,
    isError,
  } = useGetAIReviewJobResultDocsList(jobId, docsPayload);

  const rows = useMemo(() => docsResponse?.result ?? [], [docsResponse]);

  const renderNoRowsOverlay = useCallback(
    () => (
      <Box
        className="no-grid-overlay"
        data-testid="view-by-docs-no-data-overlay"
      >
        <NoDataFoundIcon aria-hidden="true" />
        <Typography
          className="typo-text headline3_size"
          data-testid="view-by-docs-no-data-message"
        >
          {t('ai.review.jobResult.messageNoData')}
        </Typography>
      </Box>
    ),
    []
  );

  const handleSelectionStateChange = useCallback(
    (state: DataGridRowSelectionModel) => {
      setSelectedIds(state.selectedIds);
      setUnselectedIds(state.unselectedIds);
      setIsAllSelected(state.isAllSelected);
    },
    []
  );

  const isViewInDocListEnabled = useMemo(
    () => isAllSelected || selectedIds.length > 0,
    [isAllSelected, selectedIds]
  );

  const navigateToDocs = useCallback(
    (
      payloadBody: IGetMasterDocIdForRelevancyPayloadBody,
      docsToView: number,
      destination: 'DOCLIST' | 'DOCVIEW'
    ) => {
      handleSetStates(
        {payloadBody, jobId},
        PAYLOAD_TYPE.DOC_RESULT_RELEVANCY,
        docsToView,
        destination
      );
    },
    [handleSetStates, jobId]
  );

  const handleViewInDocList = useCallback(() => {
    const payloadBody: IGetMasterDocIdForRelevancyPayloadBody = {
      isInverse: isAllSelected,
      selectedDocIDs: !isAllSelected ? selectedIds.join(',') : '',
      excludedDocIDs: isAllSelected ? unselectedIds.join(',') : '',
      documentCount: 0,
      columnFilters: mapGridFiltersToNavigateColumnFilters(
        filterModel.items,
        VIEW_BY_DOCS_NAVIGATE_COLUMN_MAP
      ),
    };
    const totalDocsToView =
      isAllSelected && docsResponse
        ? docsResponse.totalDocCount - unselectedIds.length
        : selectedIds.length;

    navigateToDocs(payloadBody, totalDocsToView, 'DOCLIST');
  }, [
    filterModel.items,
    docsResponse,
    isAllSelected,
    navigateToDocs,
    selectedIds,
    unselectedIds,
  ]);

  const handleDocIdClick = useCallback(
    (documentId: number, docId: number) => {
      setSelectedDocForPreview({
        id: documentId,
        docId: String(docId),
      });
    },
    [setSelectedDocForPreview]
  );

  const handleDocDetailsClick = useCallback(
    (_documentId: number, docId: number, reviewJobSubmittedId: number) => {
      setSelectedIssueDoc({docId, reviewJobSubmittedId});
    },
    []
  );

  useEffect(() => {
    onViewInDocListActionChange?.({
      enabled: isViewInDocListEnabled,
      onClick: handleViewInDocList,
    });
  }, [
    handleViewInDocList,
    isViewInDocListEnabled,
    onViewInDocListActionChange,
  ]);

  useEffect(() => {
    if (isError) {
      setToast({
        open: true,
        message: t('ai.review.jobResult.error.failedToFetch'),
        status: ToastType.Error,
      });
    }
  }, [isError]);

  const columns: GridColDef[] = useMemo(
    () =>
      getViewByDocsColumns(
        handleDocIdClick,
        handleDocDetailsClick,
        docsResponse
      ),
    [docsResponse, handleDocDetailsClick, handleDocIdClick]
  );

  return (
    <Box
      className="issue-code-container"
      data-testid="view-by-docs-data-grid-container"
    >
      <ArrDataGridPro
        rowHeight={tableProps.ROW_HEIGHT}
        rows={rows}
        columns={columns}
        getRowClassName={(params) => {
          if (isAllSelected) return 'row-checkbox-selected';
          return selectedIds.includes(String(params.id))
            ? 'row-checkbox-selected'
            : '';
        }}
        rowCount={docsResponse?.totalDocCount ?? 0}
        primaryKey="documentId"
        pagination
        paginationMode="server"
        alwaysShowPagination
        sortingMode="server"
        filterMode="server"
        loading={isLoading}
        enableCheckbox
        selectedIds={selectedIds}
        unselectedIds={unselectedIds}
        isAllSelected={isAllSelected}
        onSelectionStateChange={handleSelectionStateChange}
        itemsPerPage={pageSize}
        setItemsPerPage={setPageSize}
        currentPage={pageNumber}
        showPerPageOptions
        pageSizeOptions={tableProps.PAGE_SIZE_OPTIONS}
        onPageChange={setPageNumber}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        headerFilters
        headerFilterHeight={tableProps.FILTER_HEADER_HEIGHT}
        disableVirtualization
        disableColumnResize={false}
        disableColumnReorder
        disableColumnFilter
        noRowsOverlayCustomText={t('ai.review.jobResult.messageNoData')}
        noRowsOverlay={renderNoRowsOverlay}
        showCellVerticalBorder
        entityType={t('common.entries')}
        getRowId={(row) => row.documentId}
      />
      {toast.open && (
        <ArrToastNotification
          message={toast.message}
          status={[toast.status]}
          onClose={() => setToast(defaultToastState)}
        />
      )}
      {selectedIssueDoc && (
        <IssueCodingDocumentDetails
          showDrawer={Boolean(selectedIssueDoc)}
          onClose={() => setSelectedIssueDoc(null)}
          jobId={jobId}
          reviewJobSubmittedId={selectedIssueDoc.reviewJobSubmittedId}
          docId={selectedIssueDoc.docId}
        />
      )}
    </Box>
  );
};

export default ViewByDocsDataGrid;
